import { ArrowsClockwise } from '@phosphor-icons/react';

const monoFont = "'IBM Plex Mono', monospace";

const LAYOUT_OPTIONS = [
  {
    value: 'A',
    letter: 'A',
    name: 'Dividida',
    description: 'Duas colunas lado a lado. Ideal para contrastar séries ou variações.',
    preview: 'split-vertical',
  },
  {
    value: 'B',
    letter: 'B',
    name: 'Cascata',
    description: 'Rolagem vertical em largura total. Contemplativo e editorial.',
    preview: 'scroll-vertical',
  },
  {
    value: 'C',
    letter: 'C',
    name: 'Panorama',
    description: 'Rolagem horizontal contínua. Sensação de filmstrip e exposição.',
    preview: 'scroll-horizontal',
  },
  {
    value: 'D',
    letter: 'D',
    name: 'Mosaico',
    description: 'Faixas empilhadas de alturas variadas. Dinâmico, hierarquia clara.',
    preview: 'split-horizontal',
  },
];

interface LayoutSelectorProps {
  currentLayout: string;
  onSelect: (layout: string) => void;
  hasChange?: boolean;
  onApply?: () => void;
}

function LayoutPreviewSVG({ type, active }: { type: string; active: boolean }) {
  const c = active ? 'rgba(247,246,242,0.5)' : 'rgba(247,246,242,0.2)';
  const cs = active ? 'rgba(247,246,242,0.15)' : 'rgba(247,246,242,0.08)';
  const svgStyle = { flexShrink: 0 } as const;

  if (type === 'split-vertical') return (
    <svg width="64" height="48" viewBox="0 0 80 56" fill="none" style={svgStyle}>
      <rect x="2" y="2" width="36" height="52" fill={cs} stroke={c} strokeWidth="0.5" />
      <rect x="42" y="2" width="36" height="52" fill={cs} stroke={c} strokeWidth="0.5" />
      <line x1="40" y1="6" x2="40" y2="50" stroke={c} strokeWidth="0.5" strokeDasharray="2 2" />
    </svg>
  );

  if (type === 'scroll-vertical') return (
    <svg width="64" height="48" viewBox="0 0 80 56" fill="none" style={svgStyle}>
      <rect x="14" y="2" width="52" height="14" fill={cs} stroke={c} strokeWidth="0.5" />
      <rect x="14" y="20" width="52" height="14" fill={cs} stroke={c} strokeWidth="0.5" />
      <rect x="14" y="38" width="52" height="14" fill={cs} stroke={c} strokeWidth="0.5" />
    </svg>
  );

  if (type === 'scroll-horizontal') return (
    <svg width="64" height="48" viewBox="0 0 80 56" fill="none" style={svgStyle}>
      <rect x="2" y="10" width="17" height="36" fill={cs} stroke={c} strokeWidth="0.5" />
      <rect x="22" y="10" width="17" height="36" fill={cs} stroke={c} strokeWidth="0.5" />
      <rect x="42" y="10" width="17" height="36" fill={cs} stroke={c} strokeWidth="0.5" />
      <rect x="62" y="10" width="17" height="36" fill={cs} stroke={c} strokeWidth="0.5" />
    </svg>
  );

  if (type === 'split-horizontal') return (
    <svg width="64" height="48" viewBox="0 0 80 56" fill="none" style={svgStyle}>
      <rect x="2" y="2" width="76" height="15" fill={cs} stroke={c} strokeWidth="0.5" />
      <rect x="2" y="20" width="76" height="13" fill={cs} stroke={c} strokeWidth="0.5" />
      <rect x="2" y="36" width="76" height="17" fill={cs} stroke={c} strokeWidth="0.5" />
    </svg>
  );

  return null;
}

export function LayoutSelector({ currentLayout, onSelect, hasChange, onApply }: LayoutSelectorProps) {
  return (
    <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '0.5px solid rgba(247,246,242,0.08)' }}>
      {/* Label */}
      <div style={{
        fontFamily: monoFont, fontSize: 12, fontWeight: 300,
        textTransform: 'uppercase', letterSpacing: '0.18em',
        color: 'rgba(247,246,242,0.4)', marginBottom: 8,
      }}>
        LAYOUT
      </div>

      {/* Cards grid */}
      <div
        className="grid grid-cols-2 sm:grid-cols-4"
        style={{ gap: '0.5px', background: 'rgba(247,246,242,0.12)', marginBottom: 20 }}
      >
        {LAYOUT_OPTIONS.map((layout) => {
          const active = currentLayout === layout.value;
          return (
            <button
              key={layout.value}
              onClick={() => onSelect(layout.value)}
              style={{
                fontFamily: monoFont,
                background: active ? 'rgba(247,246,242,0.04)' : '#0A0A0A',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                padding: 0,
                transition: 'background 0.15s ease',
                borderRadius: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(247,246,242,0.03)'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = '#0A0A0A'; }}
            >
              {/* Preview area — fixed height */}
              <div style={{
                height: 80,
                padding: '12px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(247,246,242,0.03)',
                borderBottom: active ? '2px solid #F7F6F2' : '0.5px solid rgba(247,246,242,0.12)',
                flexShrink: 0,
              }}>
                <LayoutPreviewSVG type={layout.preview} active={active} />
              </div>

              {/* Text area — compact */}
              <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                <span style={{
                  fontSize: 12, letterSpacing: '0.18em',
                  color: 'rgba(247,246,242,0.35)',
                }}>
                  {layout.letter}
                </span>
                <span style={{
                  fontSize: 12, fontWeight: 300,
                  color: active ? '#F7F6F2' : 'rgba(247,246,242,0.65)',
                }}>
                  {layout.name}
                </span>
                <span className="hidden sm:block" style={{
                  fontSize: 12, fontWeight: 300, lineHeight: '1.4',
                  color: 'rgba(247,246,242,0.3)', marginTop: 2,
                }}>
                  {layout.description}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Apply button */}
      {hasChange && onApply && (
        <button
          onClick={onApply}
          style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300,
            textTransform: 'uppercase', letterSpacing: '0.1em',
            padding: '8px 16px', marginTop: 8,
            border: '0.5px solid rgba(247,246,242,0.4)',
            color: 'rgba(247,246,242,0.8)',
            backgroundColor: 'transparent',
            cursor: 'pointer', borderRadius: 0,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            transition: 'all 0.15s ease',
          }}
        >
          <ArrowsClockwise size={12} weight="thin" />
          ATUALIZAR
        </button>
      )}
    </div>
  );
}

export { LAYOUT_OPTIONS };
